// src/marketing/LandingPage.jsx
import { useState } from "react";
import { googleFont } from "../auth/authStyles";
import TermsModal from "../legal/TermsModal";
import PrivacyModal from "../legal/PrivacyModal";
import GuideModal from "./GuideModal";
import { RubricSection, AnnotatedText } from "../WritingCoach";
import { TIERS } from "../payments/tiers";
import {
  SAMPLE_GRADE_LABEL, SAMPLE_MODE_LABEL, SAMPLE_TEXT,
  SAMPLE_RESULT, SAMPLE_RUBRIC_SECTION,
} from "./sampleContent";

const SAMPLE_TABS = [
  ["feedback", "💬 Coach Feedback"],
  ["annotated", "🖊 Annotated Writing"],
  ["structure", "📊 Structure Report"],
];

const WRITING_MODE_PILLS = [
  "Single Paragraph", "5-Paragraph Essay", "Creative Narrative", "Unstructured / Free Write",
];

const LANDING_SIBLING_APPS = [
  {
    key: "sse",
    icon: "🌱",
    name: "Social Skills Explorer",
    description: "AI-powered social-emotional learning for K–9th graders — interactive scenarios "
      + "that let kids practice real-life social moments and reflect on their choices, one story at a time.",
    url: "https://socialskillsexplorer.chaicodes.com",
  },
  {
    key: "rcb",
    icon: "📖",
    name: "Reading Comprehension Buddy",
    description: "Reading Buddy turns reading practice into a game — AI-generated stories and "
      + "questions for K-8 kids, with instant feedback, reading streaks, and badges.",
    url: "https://readingcompbuddy.chaicodes.com",
  },
  {
    key: "wc",
    icon: "✍️",
    name: "Writing Coach",
    description: "Instant, grade-calibrated writing feedback for students 2–12 — it coaches, it "
      + "never rewrites a word for them.",
    current: true,
  },
];

export default function LandingPage({ onGetStarted, onLogin }) {
  const [tab, setTab] = useState("feedback");
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const tabIndex = SAMPLE_TABS.findIndex(([v]) => v === tab);
  const goPrev = () => setTab(SAMPLE_TABS[Math.max(tabIndex - 1, 0)][0]);
  const goNext = () => setTab(SAMPLE_TABS[Math.min(tabIndex + 1, SAMPLE_TABS.length - 1)][0]);

  return (
    <div style={pageStyle}>
      {googleFont}
      <style>{`html { scroll-behavior: smooth; }`}</style>

      {showTerms   && <TermsModal readOnly onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
      {showGuide   && <GuideModal onClose={() => setShowGuide(false)} />}

      {/* ── Nav ──────────────────────────────────────────────────────────────── */}
      <nav style={navStyle}>
        <div style={navInner}>
          <div style={navBrand}>
            <span style={navLogo}>✍️</span>
            <span style={navWordmark}>Writing Coach</span>
          </div>
          <div style={navLinks}>
            <a href="#expect" style={navLink}>How It Works</a>
            <a href="#sample" style={navLink}>Sample</a>
            <a href="#pricing" style={navLink}>Pricing</a>
            <a href="#trust" style={navLink}>Privacy &amp; Trust</a>
            <button onClick={() => setShowGuide(true)} style={navLinkBtn}>Guide</button>
            <button onClick={onLogin} style={navLoginBtn}>Log In</button>
            <button onClick={onGetStarted} style={navCtaBtn}>Get Started Free</button>
          </div>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section style={heroSection}>
        <div style={heroInner}>
          <h1 style={heroHeadline}>Confident writers, grade by grade.</h1>
          <p style={heroSubhead}>
            Writing Coach gives students in grades 2–12 instant, rubric-based feedback on their
            writing — coach commentary, color-coded inline annotations, and a structure report.
            It evaluates. <strong style={{ color: "#e6edf3" }}>It never rewrites or completes your
            child's work.</strong>
          </p>
          <div style={heroCtaRow}>
            <button onClick={onGetStarted} style={heroCtaPrimary}>Get Started Free →</button>
            <a href="#sample" style={heroCtaSecondary}>See a Sample Result</a>
          </div>
          <p style={heroReassurance}>Start with 1 free evaluation · No credit card required</p>

          <div style={pillRow}>
            {WRITING_MODE_PILLS.map(label => (
              <span key={label} style={pillStyle}>{label}</span>
            ))}
          </div>
          <p style={heroFinePrint}>
            Type it, upload a PDF or .docx, or snap a photo — even handwritten — and we'll read it.
          </p>
        </div>
      </section>

      {/* ── What to expect ───────────────────────────────────────────────────── */}
      <section id="expect" style={sectionWrap}>
        <h2 style={sectionHeading}>What to expect</h2>
        <p style={sectionSubhead}>
          A quick look at how an evaluation works, end to end — for students and the parents
          checking in on them.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 24 }}>
          <ExpectStep number="1" title="Submit the writing">
            Type it in, upload a PDF/.docx/.txt, or snap a photo of handwritten work. Pick a grade
            level and writing mode first so the rubric matches.
          </ExpectStep>
          <ExpectStep number="2" title="Claude evaluates it">
            The writing is scored against a rubric calibrated to that grade level — usually in well
            under a minute.
          </ExpectStep>
          <ExpectStep number="3" title="Get three feedback views">
            Coach Feedback, color-coded Annotated Writing, and a Structure Report — every result
            includes all three.
          </ExpectStep>
          <ExpectStep number="4" title="Your child revises — not us">
            Writing Coach evaluates and coaches. It never rewrites or completes the work, so the
            revising stays in your child's hands.
          </ExpectStep>
        </div>

        <p style={{ fontSize: 12, color: "#484f58", textAlign: "center", margin: "20px 0 0" }}>
          Want the full picture — pricing details, privacy, supported file types? Read the{" "}
          <button onClick={() => setShowGuide(true)} style={inlineLinkBtn}>Parent &amp; Student Guide</button>.
        </p>
      </section>

      {/* ── Sample walkthrough ───────────────────────────────────────────────── */}
      <section id="sample" style={sectionWrap}>
        <h2 style={sectionHeading}>See exactly what you'll get</h2>
        <p style={sectionSubhead}>
          Here's a real sample evaluation for a {SAMPLE_GRADE_LABEL.toLowerCase()} {SAMPLE_MODE_LABEL.toLowerCase()}
          {" "}— no sign-up needed. Click through all three tabs, or use Next.
        </p>

        <div style={sampleCard}>
          <div style={sampleHeaderRow}>
            <div>
              <div style={sampleBadge}>{SAMPLE_GRADE_LABEL} · {SAMPLE_MODE_LABEL}</div>
              <p style={sampleText}>{SAMPLE_TEXT}</p>
            </div>
          </div>

          <div style={sampleNotice}>Sample result — not a live evaluation. No data is sent anywhere on this page.</div>

          <div style={stepRow}>
            <span style={stepLabel}>Step {tabIndex + 1} of {SAMPLE_TABS.length}</span>
            <div style={stepButtons}>
              <button onClick={goPrev} disabled={tabIndex === 0} style={stepBtnStyle(tabIndex === 0)}>← Prev</button>
              <button onClick={goNext} disabled={tabIndex === SAMPLE_TABS.length - 1} style={stepBtnStyle(tabIndex === SAMPLE_TABS.length - 1)}>Next →</button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #21262d" }}>
            {SAMPLE_TABS.map(([val, lbl]) => (
              <button key={val} onClick={() => setTab(val)} style={{
                padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                background: "transparent", border: "none",
                borderBottom: tab === val ? "2px solid #388bfd" : "2px solid transparent",
                color: tab === val ? "#388bfd" : "#8b949e", transition: "all 0.15s",
                fontFamily: "'DM Sans', sans-serif",
              }}>{lbl}</button>
            ))}
          </div>

          {tab === "feedback" && (
            <div style={tabPanelStyle}>
              <div style={tabPanelLabel}>Coach Commentary</div>
              <p style={{ fontSize: 15, lineHeight: 1.9, color: "#c9d1d9", whiteSpace: "pre-wrap", margin: "0 0 22px" }}>
                {SAMPLE_RESULT.coachFeedback}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
                <div style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: 10, padding: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#4ade80", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 10 }}>✓ Strengths</div>
                  {SAMPLE_RESULT.strengths.map((s, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 13, color: "#c9d1d9", lineHeight: 1.55 }}>
                      <span style={{ color: "#4ade80", flexShrink: 0 }}>✓</span>{s}
                    </div>
                  ))}
                </div>
                <div style={{ background: "rgba(251,146,60,0.06)", border: "1px solid rgba(251,146,60,0.2)", borderRadius: 10, padding: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#fb923c", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 10 }}>→ Opportunities</div>
                  {SAMPLE_RESULT.opportunities.map((o, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 13, color: "#c9d1d9", lineHeight: 1.55 }}>
                      <span style={{ color: "#fb923c", flexShrink: 0 }}>→</span>{o}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "annotated" && (
            <div style={tabPanelStyle}>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18, alignItems: "center" }}>
                {[
                  { color: "#4ade80", label: "Strength" },
                  { color: "#fb923c", label: "Improve" },
                  { color: "#60a5fa", label: "Structure" },
                  { color: "#c084fc", label: "Style / Syntax" },
                ].map(item => (
                  <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                    <span style={{ width: 11, height: 11, borderRadius: 3, background: item.color, display: "inline-block" }} />
                    <span style={{ color: "#8b949e" }}>{item.label}</span>
                  </div>
                ))}
                <span style={{ color: "#484f58", fontSize: 11 }}>· Tap highlighted text to see comment</span>
              </div>
              <div style={{ background: "#0d1117", borderRadius: 10, padding: "20px 22px", border: "1px solid #21262d" }}>
                <AnnotatedText annotated={SAMPLE_RESULT.annotated} />
              </div>
            </div>
          )}

          {tab === "structure" && (
            <div style={tabPanelStyle}>
              <div style={tabPanelLabel}>Structure Rubric — {SAMPLE_MODE_LABEL}</div>
              <RubricSection
                section={SAMPLE_RUBRIC_SECTION}
                ratings={SAMPLE_RESULT.criteriaRatings}
                feedback={SAMPLE_RESULT.criteriaFeedback}
              />
              <p style={{ fontSize: 12, color: "#484f58", margin: "8px 0 0", lineHeight: 1.6 }}>
                Grades 6 and up also unlock a Style &amp; Mechanics (or Literary Devices) section with
                deeper feedback on sentence variety, word choice, and craft.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── Pricing ──────────────────────────────────────────────────────────── */}
      <section id="pricing" style={sectionWrap}>
        <h2 style={sectionHeading}>Simple, transparent pricing</h2>
        <p style={sectionSubhead}>
          1 token = 1 evaluation · Tokens never expire · Shared across Writing Coach, SSE, and
          Reading Comp Buddy
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginTop: 24 }}>
          {TIERS.map(tier => (
            <div key={tier.tokens} style={{
              border:       `1px solid ${tier.highlight ? "#388bfd" : "#30363d"}`,
              borderRadius: 12, padding: 18, position: "relative",
              background:   tier.highlight ? "rgba(56,139,253,0.08)" : "#161b22",
            }}>
              {tier.highlight && (
                <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: "#388bfd", color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 99, whiteSpace: "nowrap" }}>
                  MOST POPULAR
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{tier.label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: "#e6edf3", lineHeight: 1 }}>
                {tier.tokens}<span style={{ fontSize: 13, color: "#8b949e", fontWeight: 400 }}> tokens</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#388bfd", margin: "6px 0 4px" }}>{tier.price}</div>
              <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 14 }}>{tier.desc}</div>
              <button onClick={onGetStarted} style={pricingCtaBtn(tier.highlight)}>
                Get Started Free
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust & Compliance ───────────────────────────────────────────────── */}
      <section id="trust" style={sectionWrap}>
        <h2 style={sectionHeading}>Built with student privacy in mind</h2>
        <p style={sectionSubhead}>
          We know parents and schools need to trust a tool before a single word gets typed into it.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 24 }}>
          <TrustCard icon="🔒" title="No stored writing">
            Submitted writing is sent to Anthropic's Claude API only to generate the evaluation.
            Writing Coach does not keep a copy of student writing after the result is returned.
          </TrustCard>
          <TrustCard icon="🏫" title="FERPA-aware for schools">
            When used in a classroom, the school is the data controller. We act as a school
            official with a legitimate educational interest and never disclose student work
            without authorization.
          </TrustCard>
          <TrustCard icon="👨‍👩‍👧" title="COPPA-conscious for families">
            Children under 13 use Writing Coach only under the supervision of a parent, guardian,
            or licensed educator, with consent obtained at signup — exactly as COPPA requires.
          </TrustCard>
          <TrustCard icon="✍️" title="Built for honest learning">
            Writing Coach evaluates and coaches — it never rewrites or completes a student's work.
            Submitting AI-generated or plagiarized writing as your own defeats the purpose of the
            assignment, and isn't supported or endorsed by Writing Coach or its creators.
          </TrustCard>
        </div>

        <div style={trustLinkRow}>
          <button onClick={() => setShowPrivacy(true)} style={trustLinkBtn}>Read Privacy Policy</button>
          <button onClick={() => setShowTerms(true)}   style={trustLinkBtn}>Read Terms of Service</button>
        </div>
      </section>

      {/* ── Ecosystem ─────────────────────────────────────────────────────────── */}
      <section id="ecosystem" style={sectionWrap}>
        <div style={{ textAlign: "center" }}>
          <span style={eyebrowPill}>One Account, Three Apps</span>
        </div>
        <h2 style={sectionHeading}>Your tokens work across the whole Chai Codes family</h2>
        <p style={sectionSubhead}>
          Tokens you buy here aren't locked to one app. The same balance carries over to every
          Chai Codes learning app for your child — buy once, practice everywhere.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 24 }}>
          {LANDING_SIBLING_APPS.map(app => (
            <EcosystemCard key={app.key} app={app} onGetStarted={onGetStarted} />
          ))}
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer style={footerStyle}>
        <span>© {new Date().getFullYear()} Chaicodes.com</span>
        <span style={{ color: "#30363d" }}>·</span>
        <a href="mailto:support@chaicodes.com" style={footerLink}>support@chaicodes.com</a>
        <span style={{ color: "#30363d" }}>·</span>
        <button onClick={() => setShowGuide(true)} style={footerLinkBtn}>Parent &amp; Student Guide</button>
        <span style={{ color: "#30363d" }}>·</span>
        <button onClick={() => setShowPrivacy(true)} style={footerLinkBtn}>Privacy Policy</button>
        <span style={{ color: "#30363d" }}>·</span>
        <button onClick={() => setShowTerms(true)} style={footerLinkBtn}>Terms of Service</button>
      </footer>
    </div>
  );
}

// ── Sub-component ───────────────────────────────────────────────────────────────
function ExpectStep({ number, title, children }) {
  return (
    <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
      <div style={{
        width: 26, height: 26, borderRadius: 99, background: "rgba(56,139,253,0.12)",
        border: "1px solid rgba(56,139,253,0.3)", color: "#388bfd", fontSize: 12, fontWeight: 700,
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10,
      }}>{number}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#e6edf3", marginBottom: 8 }}>{title}</div>
      <p style={{ fontSize: 13, color: "#8b949e", lineHeight: 1.65, margin: 0 }}>{children}</p>
    </div>
  );
}

function TrustCard({ icon, title, children }) {
  return (
    <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 22, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#e6edf3", marginBottom: 8 }}>{title}</div>
      <p style={{ fontSize: 13, color: "#8b949e", lineHeight: 1.65, margin: 0 }}>{children}</p>
    </div>
  );
}

function EcosystemCard({ app, onGetStarted }) {
  return (
    <div style={{
      background: "#161b22",
      border: app.current ? "1px solid #388bfd" : "1px solid #21262d",
      borderRadius: 12, padding: 20,
      boxShadow: app.current ? "0 0 0 1px rgba(56,139,253,0.15)" : "none",
      position: "relative",
    }}>
      {app.current && (
        <div style={{
          position: "absolute", top: -10, right: 16, background: "#388bfd", color: "#fff",
          fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
        }}>YOU'RE HERE</div>
      )}
      <div style={{
        width: 40, height: 40, borderRadius: 99, background: "rgba(56,139,253,0.1)",
        border: "1px solid rgba(56,139,253,0.3)", display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: 18, marginBottom: 12,
      }} aria-hidden="true">{app.icon}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#e6edf3", marginBottom: 8 }}>{app.name}</div>
      <p style={{ fontSize: 13, color: "#8b949e", lineHeight: 1.65, margin: "0 0 16px" }}>{app.description}</p>
      {app.current ? (
        <button onClick={onGetStarted} style={ecosystemCtaBtn(true)}>Get Started</button>
      ) : (
        <a href={app.url} target="_blank" rel="noopener noreferrer" style={ecosystemCtaBtn(false)}>
          Visit {app.name} ↗
        </a>
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────────
const pageStyle = {
  minHeight: "100vh",
  background: "linear-gradient(160deg, #0d1117 0%, #161b22 60%, #0d1117 100%)",
  fontFamily: "'DM Sans', sans-serif",
  color: "#e6edf3",
};

const navStyle = {
  position: "sticky", top: 0, zIndex: 100,
  background: "rgba(13,17,23,0.92)", backdropFilter: "blur(8px)",
  borderBottom: "1px solid #21262d",
};

const navInner = {
  maxWidth: 1100, margin: "0 auto", padding: "14px 24px",
  display: "flex", alignItems: "center", justifyContent: "space-between",
  flexWrap: "wrap", gap: 12,
};

const navBrand = { display: "flex", alignItems: "center", gap: 8 };
const navLogo = { fontSize: 22 };
const navWordmark = { fontFamily: "'DM Serif Display', serif", fontSize: 18, color: "#e6edf3" };

const navLinks = { display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" };
const navLink = { color: "#8b949e", fontSize: 13, textDecoration: "none" };

const navLinkBtn = {
  background: "transparent", border: "none", color: "#8b949e", fontSize: 13,
  cursor: "pointer", padding: 0, fontFamily: "'DM Sans', sans-serif",
};

const navLoginBtn = {
  background: "transparent", border: "1px solid #30363d", color: "#e6edf3",
  borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};

const navCtaBtn = {
  background: "linear-gradient(135deg, #1f6feb, #388bfd)", border: "none", color: "#fff",
  borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};

const heroSection = { padding: "72px 24px 56px", textAlign: "center" };
const heroInner = { maxWidth: 720, margin: "0 auto" };

const heroHeadline = {
  fontFamily: "'DM Serif Display', serif", fontWeight: 400,
  fontSize: "clamp(28px, 5vw, 44px)", color: "#e6edf3", margin: "0 0 18px", lineHeight: 1.2,
};

const heroSubhead = { fontSize: 16, color: "#8b949e", lineHeight: 1.7, margin: "0 0 28px" };

const heroCtaRow = { display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: 14 };

const heroCtaPrimary = {
  background: "linear-gradient(135deg, #1f6feb, #388bfd)", border: "none", color: "#fff",
  borderRadius: 9, padding: "13px 26px", fontSize: 15, fontWeight: 700, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};

const heroCtaSecondary = {
  border: "1px solid #30363d", color: "#e6edf3", borderRadius: 9, padding: "13px 26px",
  fontSize: 15, fontWeight: 600, textDecoration: "none", fontFamily: "'DM Sans', sans-serif",
};

const heroReassurance = { fontSize: 13, color: "#4ade80", margin: "0 0 30px" };

const pillRow = { display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 14 };

const pillStyle = {
  border: "1px solid #30363d", borderRadius: 99, padding: "6px 14px",
  fontSize: 12, color: "#c9d1d9", background: "#161b22",
};

const heroFinePrint = { fontSize: 12, color: "#484f58", margin: 0 };

const sectionWrap = { maxWidth: 980, margin: "0 auto", padding: "56px 24px" };

const sectionHeading = {
  fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: 28, color: "#e6edf3",
  textAlign: "center", margin: "0 0 10px",
};

const sectionSubhead = { fontSize: 14, color: "#8b949e", textAlign: "center", maxWidth: 620, margin: "0 auto", lineHeight: 1.6 };

const sampleCard = {
  marginTop: 28, background: "#161b22", border: "1px solid #21262d", borderRadius: 16,
  padding: 22, overflow: "hidden",
};

const sampleHeaderRow = { marginBottom: 14 };

const sampleBadge = {
  display: "inline-block", fontSize: 11, fontWeight: 700, color: "#8b949e",
  letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 8,
};

const sampleText = {
  fontSize: 14, color: "#c9d1d9", lineHeight: 1.7, fontStyle: "italic",
  background: "#0d1117", border: "1px solid #21262d", borderRadius: 10, padding: "14px 16px", margin: 0,
};

const sampleNotice = {
  fontSize: 12, color: "#fb923c", background: "rgba(251,146,60,0.08)",
  border: "1px solid rgba(251,146,60,0.25)", borderRadius: 8, padding: "8px 12px", marginBottom: 16,
};

const stepRow = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 };
const stepLabel = { fontSize: 12, color: "#8b949e", fontWeight: 600 };
const stepButtons = { display: "flex", gap: 8 };

const stepBtnStyle = (disabled) => ({
  border: "1px solid #30363d", background: "transparent",
  color: disabled ? "#484f58" : "#e6edf3", borderRadius: 7, padding: "6px 12px",
  fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: "'DM Sans', sans-serif",
});

const tabPanelStyle = {
  background: "#161b22", border: "1px solid #21262d", borderTop: "none",
  borderRadius: "0 0 12px 12px", padding: 22,
};

const tabPanelLabel = {
  fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1.1,
  textTransform: "uppercase", marginBottom: 14,
};

const pricingCtaBtn = (highlight) => ({
  width: "100%", padding: "8px", borderRadius: 8, fontSize: 13, fontWeight: 700,
  border: "none", cursor: "pointer",
  background: highlight ? "linear-gradient(135deg, #1f6feb, #388bfd)" : "#21262d",
  color: "#fff", fontFamily: "'DM Sans', sans-serif",
});

const trustLinkRow = { display: "flex", gap: 16, justifyContent: "center", marginTop: 28, flexWrap: "wrap" };

const trustLinkBtn = {
  background: "transparent", border: "1px solid #30363d", color: "#388bfd",
  borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};

const eyebrowPill = {
  display: "inline-block",
  fontSize: 11, fontWeight: 700, color: "#388bfd", letterSpacing: 1, textTransform: "uppercase",
  background: "rgba(56,139,253,0.08)", border: "1px solid rgba(56,139,253,0.25)",
  borderRadius: 99, padding: "5px 14px", marginBottom: 14,
};

const ecosystemCtaBtn = (highlight) => ({
  display: "inline-block", width: "100%", textAlign: "center", boxSizing: "border-box",
  padding: "8px", borderRadius: 8, fontSize: 13, fontWeight: 700,
  border: "none", cursor: "pointer", textDecoration: "none",
  background: highlight ? "linear-gradient(135deg, #1f6feb, #388bfd)" : "#21262d",
  color: "#fff", fontFamily: "'DM Sans', sans-serif",
});

const footerStyle = {
  borderTop: "1px solid #21262d", padding: "26px 24px", textAlign: "center",
  display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap",
  fontSize: 12, color: "#8b949e",
};

const footerLink = { color: "#8b949e", textDecoration: "none" };

const footerLinkBtn = {
  background: "transparent", border: "none", color: "#8b949e", cursor: "pointer",
  fontSize: 12, padding: 0, fontFamily: "'DM Sans', sans-serif", textDecoration: "underline",
};

const inlineLinkBtn = {
  background: "transparent", border: "none", color: "#388bfd", cursor: "pointer",
  fontSize: 12, padding: 0, fontFamily: "'DM Sans', sans-serif", textDecoration: "underline",
};
